<template>
  <div v-if="messages.length" class="form-messages-overlay" data-testid="form-messages-overlay">
    <div
      v-for="message in messages"
      :key="message.id"
      class="form-messages-overlay__balloon"
      :class="`form-messages-overlay__balloon--${message.type}`"
      role="status"
    >
      <pre class="form-messages-overlay__text">{{ message.text }}</pre>
      <div v-if="message.autoCountdownMs != null" class="form-messages-overlay__countdown">
        <div class="form-messages-overlay__countdown-track">
          <div
            class="form-messages-overlay__countdown-bar"
            :style="{ width: `${getCountdownPercent(message)}%` }"
          />
        </div>
        <span class="form-messages-overlay__countdown-label">{{ getCountdownSecondsLabel(message.id) }}</span>
      </div>
      <div class="form-messages-overlay__actions">
        <button
          v-for="action in message.actions"
          :key="action"
          type="button"
          class="form-messages-overlay__action"
          :class="`form-messages-overlay__action--${action}`"
          :disabled="isMessageBusy(message.id)"
          @click.stop="onChoose(message, action)"
        >
          {{ formMessageActionLabel(action) }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onUnmounted, reactive, ref, watch } from 'vue'
import { usePageData } from '@/common/composables/usePageData'
import {
  formMessageActionLabel,
  type FormMessageActionType,
  type FormSoftMessage,
} from '@/services/form-events/formSoftEvent'
import { useFormSoftMessages } from '@/services/form-events/formMessagesStore'
import { executeFormSoftMessageAction } from '@/services/form-events/formEventRouter'

const { pageDataSlot } = usePageData()
const messages = useFormSoftMessages(() => pageDataSlot.value)

const busyMessageIds = ref(new Set<string>())

function isMessageBusy(messageId: string): boolean {
  return busyMessageIds.value.has(messageId)
}

const countdownRemainingMs = reactive(new Map<string, number>())
const countdownTotalMs = new Map<string, number>()
const countdownIntervals = new Map<string, ReturnType<typeof setInterval>>()

const TICK_MS = 100

function getCountdownPercent(message: FormSoftMessage): number {
  const total = countdownTotalMs.get(message.id) ?? message.autoCountdownMs ?? 3000
  const remaining = countdownRemainingMs.get(message.id) ?? total
  return Math.max(0, (remaining / total) * 100)
}

function getCountdownSecondsLabel(messageId: string): string {
  const remaining = countdownRemainingMs.get(messageId) ?? 0
  return `${Math.ceil(remaining / 1000)}`
}

function clearCountdown(messageId: string): void {
  const interval = countdownIntervals.get(messageId)
  if (interval != null) {
    clearInterval(interval)
    countdownIntervals.delete(messageId)
  }
  countdownRemainingMs.delete(messageId)
  countdownTotalMs.delete(messageId)
}

function startCountdown(message: FormSoftMessage): void {
  if (!message.autoCountdownMs || !message.autoCountdownAction) return
  const id = message.id
  if (countdownIntervals.has(id)) return

  const totalMs = message.autoCountdownMs
  countdownTotalMs.set(id, totalMs)
  countdownRemainingMs.set(id, totalMs)

  const interval = setInterval(() => {
    const current = countdownRemainingMs.get(id) ?? 0
    const next = current - TICK_MS
    if (next <= 0) {
      clearCountdown(id)
      void triggerAutoAction(message)
    } else {
      countdownRemainingMs.set(id, next)
    }
  }, TICK_MS)

  countdownIntervals.set(id, interval)
}

async function triggerAutoAction(message: FormSoftMessage): Promise<void> {
  if (!message.autoCountdownAction) return
  if (busyMessageIds.value.has(message.id)) return

  const next = new Set(busyMessageIds.value)
  next.add(message.id)
  busyMessageIds.value = next

  try {
    await executeFormSoftMessageAction(message, message.autoCountdownAction)
  } finally {
    if (busyMessageIds.value.has(message.id)) {
      const cleared = new Set(busyMessageIds.value)
      cleared.delete(message.id)
      busyMessageIds.value = cleared
    }
  }
}

watch(
  messages,
  (newMessages, oldMessages) => {
    const oldIds = new Set((oldMessages ?? []).map((m) => m.id))
    const newIds = new Set(newMessages.map((m) => m.id))

    for (const msg of newMessages) {
      if (!oldIds.has(msg.id) && msg.autoCountdownMs) {
        startCountdown(msg)
      }
    }

    for (const msg of oldMessages ?? []) {
      if (!newIds.has(msg.id)) {
        clearCountdown(msg.id)
      }
    }
  },
  { immediate: true },
)

onUnmounted(() => {
  for (const interval of countdownIntervals.values()) {
    clearInterval(interval)
  }
  countdownIntervals.clear()
})

async function onChoose(message: FormSoftMessage, action: FormMessageActionType): Promise<void> {
  if (busyMessageIds.value.has(message.id)) return

  clearCountdown(message.id)

  const next = new Set(busyMessageIds.value)
  next.add(message.id)
  busyMessageIds.value = next

  try {
    await executeFormSoftMessageAction(message, action)
  } finally {
    if (busyMessageIds.value.has(message.id)) {
      const cleared = new Set(busyMessageIds.value)
      cleared.delete(message.id)
      busyMessageIds.value = cleared
    }
  }
}
</script>

<style scoped lang="scss">
.form-messages-overlay {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1200;
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: min(320px, calc(100% - 16px));
  pointer-events: none;
}

.form-messages-overlay__balloon {
  pointer-events: auto;
  border: 0;
  border-radius: 10px;
  padding: 14px;
  text-align: left;
  box-shadow: 0 4px 12px rgb(0 0 0 / 28%);
  font: inherit;
}

.form-messages-overlay__balloon--alert {
  background: rgb(255, 220, 50);
  color: #111;
}

.form-messages-overlay__balloon--error {
  background: rgb(235, 86, 87);
  color: #fff;
}

.form-messages-overlay__balloon--info {
  background: rgb(65, 65, 65);
  color: #fff;
}

.form-messages-overlay__text {
  margin: 0 0 10px;
  white-space: pre-wrap;
  font-family: inherit;
  font-size: 0.875rem;
  line-height: 1.35;
}

.form-messages-overlay__countdown {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.form-messages-overlay__countdown-track {
  flex: 1;
  height: 4px;
  border-radius: 2px;
  background: rgb(0 0 0 / 15%);
  overflow: hidden;
}

.form-messages-overlay__balloon--error .form-messages-overlay__countdown-track,
.form-messages-overlay__balloon--info .form-messages-overlay__countdown-track {
  background: rgb(255 255 255 / 20%);
}

.form-messages-overlay__countdown-bar {
  height: 100%;
  border-radius: 2px;
  background: rgb(0 0 0 / 35%);
  transition: width 0.1s linear;
}

.form-messages-overlay__balloon--error .form-messages-overlay__countdown-bar,
.form-messages-overlay__balloon--info .form-messages-overlay__countdown-bar {
  background: rgb(255 255 255 / 60%);
}

.form-messages-overlay__countdown-label {
  font-size: 0.75rem;
  opacity: 0.6;
  min-width: 1.2em;
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.form-messages-overlay__actions {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-messages-overlay__action {
  width: 100%;
  border: 1px solid rgb(0 0 0 / 25%);
  border-radius: 6px;
  padding: 0.45rem 0.6rem;
  background: rgb(255 255 255 / 85%);
  color: #111;
  font: inherit;
  font-size: 0.8125rem;
  cursor: pointer;
  text-align: center;
}

.form-messages-overlay__balloon--error .form-messages-overlay__action,
.form-messages-overlay__balloon--info .form-messages-overlay__action {
  background: rgb(255 255 255 / 92%);
}

.form-messages-overlay__action--close_form {
  border-color: rgb(160 40 40 / 45%);
}

.form-messages-overlay__action:hover:not(:disabled) {
  filter: brightness(0.97);
}

.form-messages-overlay__action:active:not(:disabled) {
  filter: brightness(0.93);
}

.form-messages-overlay__action:disabled {
  opacity: 0.55;
  cursor: default;
}
</style>
